import React, { useState, useCallback, useEffect } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import { validateAddEditForm } from './validations';
import Tabs from './components/Tabs';
import UserDetailsTab from './components/UserDetailsTab';
import Header from './components/Header';
import Buttons from './components/Buttons';
import useFetch from '../../../../hooks/useFetch';
import {
  VALIDATION_FIELD_MAPPING,
  APP_SUCCESS_MESSAGES,
} from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi, UserResponse } from '../../../../interfaces/user.interface';
import { Validation } from '../../../../interfaces/common';
import UserService from '../../../../services/user.service';
import RoleService from '../../../../services/role.service';
import CommonService from '../../../../services/common.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { STATUS_CODE } from '../../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  contactNumber: '',
  countryCode: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  status: true,
  password: '',
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  role: '',
  salutation: '',
  city: '',
  state: '',
};

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.users + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.users + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [tab, setTab] = useState<'userDetails'>('userDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);

  useEffect(() => {
    setTab(location?.state?.tab ?? 'userDetails');
  }, [location.state, location.key]);

  const { data: allRoles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      search: '',
    },
    dependency: [],
  });

  const { data: allSalutations } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: {
      key: 'salutation',
      searchText: '',
    },
    dependency: [],
  });

  const { data: salutations } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: {
      key: 'salutation',
      searchText: dropdownSearchData.salutation,
    },
    dependency: [dropdownSearchData.salutation],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'city',
      searchText: '',
    },
    dependency: [],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'city',
      searchText: dropdownSearchData.city || '',
    },
    dependency: [dropdownSearchData.city],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'state',
      searchText: '',
    },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'state',
      searchText: dropdownSearchData.state || '',
    },
    dependency: [dropdownSearchData.state],
  });

  const { data: roles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      search: dropdownSearchData.role,
    },
    dependency: [dropdownSearchData.role],
  });

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
  }, [location.key]);

  const { loading: getContentLoading } = useFetch({
    service: UserService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: UserResponse) => {
      setFormData({
        salutation: response.salutation?.code,
        firstName: response.first_name,
        lastName: response.last_name,
        email: response.email,
        role: response.role?.id ?? '',
        contactNumber: response.contact_number,
        countryCode: '',
        address: response.address,
        city: response.city?.code,
        state: response.state?.code,
        pincode: response.pincode,
        status: response.is_active,
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.users);
    },
  });

  const handleTabChange = useCallback((e: any, value: 'userDetails') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      if (name === VALIDATION_FIELD_MAPPING.contactNumber) {
        const phoneNumber = parsePhoneNumber(event?.target?.value ?? '');

        setFormData((prev) => {
          return {
            ...prev,
            [VALIDATION_FIELD_MAPPING.contactNumber]: phoneNumber && phoneNumber.nationalNumber,
            [VALIDATION_FIELD_MAPPING.countryCode]: phoneNumber
              ? `+${phoneNumber && phoneNumber.countryCallingCode}`
              : '',
          };
        });
      } else {
        let value: any;

        if (name === VALIDATION_FIELD_MAPPING.status) {
          value = event.target.checked;
        } else {
          value = event.target.value;
        }

        setFormData((prev) => {
          return {
            ...prev,
            [name]: value,
          };
        });
      }
    },
    [setFormData]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name,
        edit
      );
      setValidation(newValidation);
    },
    [formData, edit]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData, undefined, edit);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const payload: AddEditApi = {
          salutation: formData.salutation,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          role: Number(formData.role),
          contact_number: (formData.countryCode || '') + (formData.contactNumber || '').trim(),
          address: formData.address?.trim(),
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode?.trim(),
          email: formData.email!.trim(),
          is_active: formData.status,
          password: formData.password,
        };

        if (edit) {
          delete payload.password;
        }

        let response: any;
        if (!edit) {
          response = await UserService.add(payload);
        } else if (id) {
          response = await UserService.edit(id, payload);
        }

        if (response.success !== false) {
          toast.success(
            <Toast
              message={edit ? APP_SUCCESS_MESSAGES.userUpdated : APP_SUCCESS_MESSAGES.userCreated}
            />,
            { icon: false }
          );

          if (edit) {
            navigate(Modules.resources + AuthRoutes.users);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.firstName);
            input && input.focus();
            navigate(Modules.resources + AuthRoutes.users + RouteParamsTypes.add, {
              replace: true,
            });
          } else {
            const createdId = response?.id;
            navigate(`${Modules.resources}${AuthRoutes.users}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.resources + AuthRoutes.users);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'userDetails') {
    formContent = (
      <UserDetailsTab
        edit={edit}
        view={view}
        allRoles={allRoles}
        roles={roles}
        allSalutations={allSalutations || []}
        salutations={salutations || []}
        allCities={allCities || []}
        cities={cities || []}
        allStates={allStates || []}
        states={states || []}
        formData={formData}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
      />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('users.addEdit.pageTexts.title.addTitle')
            : view
              ? t('users.addEdit.pageTexts.title.viewTitle')
              : t('users.addEdit.pageTexts.title.editTitle')}
        </h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default AddEditView;
